import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatDateTime } from "@/lib/format";
import { Card, PageHeader, Badge } from "@/components/ui-kit";
import { usePagination } from "@/hooks/usePagination";

// Safe render: log entries may have non-string target/action/detail (older docs
// stored objects). Coerce to display string to avoid "Objects are not valid as
// a React child" crash → blank white page.
const safeStr = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
};

export default function SystemLogs() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/logs?limit=2000")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message || "Gagal memuat log"))
      .finally(() => setLoading(false));
  }, []);

  // Iter v28 #1 — 25 per page
  const { pageItems, Pagination } = usePagination(rows, { perPage: 25 });

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader overline="Admin · Audit" title={t("logs")} description="Catatan komprehensif aktivitas pengguna pada seluruh modul sistem." />

      {err && (
        <div className="p-3 border border-red-200 bg-red-50 text-sm text-red-700" data-testid="log-error">
          Gagal memuat log: {err}
        </div>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">Waktu</th>
              <th className="py-3 px-3">User</th>
              <th className="py-3 px-3">Aksi</th>
              <th className="py-3 px-3">Target</th>
              <th className="py-3 px-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="py-12 text-center text-stone-400">Memuat…</td></tr>}
            {!loading && pageItems.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-stone-400">Belum ada log.</td></tr>}
            {pageItems.map((r, idx) => (
              <tr key={r.id || idx} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`log-row-${idx}`}>
                <td className="py-2.5 px-3 font-mono text-xs text-stone-600 whitespace-nowrap">{formatDateTime(r.ts)}</td>
                <td className="py-2.5 px-3 font-mono">{safeStr(r.user_email)}</td>
                <td className="py-2.5 px-3"><Badge tone="terra">{safeStr(r.action)}</Badge></td>
                <td className="py-2.5 px-3 font-mono text-xs text-stone-600">{safeStr(r.target) || "—"}</td>
                <td className="py-2.5 px-3 text-xs text-stone-500 max-w-md truncate">{safeStr(r.detail || {})}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {Pagination}
      </Card>
    </div>
  );
}
